import React, {useState, useMemo, useEffect} from "react";
import {Table, Input, Button, Space, Checkbox, Tooltip, message} from "antd";
import { DeviData } from "../../../../types/DeviData";
import { House } from "../../../../types/House";
import './DeviToOGLAssignment.css';
import {
    ArrowRightOutlined,
    CheckCircleOutlined,
    CheckOutlined, CloseCircleOutlined,
    DeleteOutlined, FileExcelOutlined, FullscreenExitOutlined, FullscreenOutlined,
    PlusOutlined,
    TableOutlined
} from "@ant-design/icons";
import {AssignmentData} from "../../../../types/AssignmentData";
import {JobCode} from "../../../../types/JobCodes";
import axios from "axios";
import {getConfig} from "../../../../config";

const StepThree: React.FC<{
    deviData: DeviData[];
    houses: House[];
    selectedProject: string | undefined;
    assignmentData: AssignmentData[];
    setAssignmentData: React.Dispatch<React.SetStateAction<AssignmentData[]>>;
    jobCodes: JobCode[];

}> = ({ deviData, houses, assignmentData, setAssignmentData, jobCodes, selectedProject  }) => {
    const mapJobCodes = (jobCodes: JobCode[]) => {
        return Object.fromEntries(jobCodes.map(job => [job.alias, job]));
    };
    const jobMap = mapJobCodes(jobCodes);
    const initialProducts = new Set(deviData.map(record => jobMap[record.jobCode!]?.descriptionSpez || "Unbekannt"));
    const initialLocations = new Set(["I", "A", "U"]);
    const initialHouses = new Set(houses.map(house => house.id));
    const [visibleHouses, setVisibleHouses] = useState<Set<number>>(initialHouses);
    const [visibleProducts, setVisibleProducts] = useState<Set<string>>(initialProducts);
    const [visibleLocations, setVisibleLocations] = useState<Set<string>>(initialLocations);
    const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);
    const [filteredData, setFilteredData] = useState<DeviData[]>(deviData);
    const [showFullText, setShowFullText] = useState(false);
    const { API_BASE_URL } = getConfig();
    const AUTH_TOKEN = "VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU";

    const clearAllAssignments = () => {
        setAssignmentData([]);
        message.info("Alle Zuweisungen wurden gelöscht.");
    };

    const toggleHouseVisibility = (houseId: number) => {
        setVisibleHouses((prev) => {
            const updated = new Set(prev);
            if (updated.has(houseId)) {
                updated.delete(houseId);
            } else {
                updated.add(houseId);
            }
            return updated;
        });
    };

    const toggleProductVisibility = (product: string) => {
        setVisibleProducts((prev) => {
            const updated = new Set(prev);
            if (updated.has(product)) {
                updated.delete(product);
            } else {
                updated.add(product);
            }
            return updated;
        });
    };

    const toggleLocationVisibility = (location: string) => {
        setVisibleLocations((prev) => {
            const updated = new Set(prev);
            if (updated.has(location)) {
                updated.delete(location);
            } else {
                updated.add(location);
            }
            return updated;
        });
    };

    const filterData = () => {
        let filtered = deviData;
        if (showIncompleteOnly) {
            filtered = filtered.filter((record) => {
                const totalAssigned = assignmentData
                    .filter((entry) => entry.projectId === record.id)
                    .reduce((sum, entry) => sum + entry.amount, 0);
                return totalAssigned < Number(record.amountDevis);
            });
        }
        if (visibleProducts.size === 0) {
            filtered = [];
        } else if (visibleProducts.size > 0 && visibleProducts.size !== initialProducts.size) {
            filtered = filtered.filter((record) => {
                if (!record.jobCode) return false;
                const product = jobMap[record.jobCode]?.descriptionSpez || "Unbekannt";
                return visibleProducts.has(product);
            });
        }
        if (visibleLocations.size === 0) {
            filtered = [];
        } else if (visibleLocations.size > 0 && visibleLocations.size !== initialLocations.size) {
            filtered = filtered.filter((record) => {
                if (!record.jobCode) return false;
                const location = mapInstallationLocation(jobMap[record.jobCode]?.installationLocation || "Innen");
                return visibleLocations.has(location);
            });
        }
        setFilteredData(filtered);
    };


    useEffect(() => {
        filterData();
    }, [showIncompleteOnly, assignmentData, deviData, visibleProducts, visibleLocations, visibleHouses]);


    useEffect(() => {
        filterIncompleteRows();
    }, [showIncompleteOnly, assignmentData, deviData]);


    const handleAssignmentChange = (
        record: DeviData,
        house: House,
        section: { name: string },
        value: number
    ) => {
        const totalAssigned = assignmentData
            .filter((entry) => entry.projectId === record.id)
            .reduce((sum, entry) => sum + entry.amount, 0);
        const remaining = Number(record.amountDevis) - totalAssigned +
            (assignmentData.find(entry =>
                entry.projectId === record.id &&
                entry.houseId === house.id &&
                entry.sectionName === section.name
            )?.amount || 0);
        if (value > remaining) return;
        setAssignmentData((prev) => {
            const entryId = `${record.id}-${house.id}-${section.name}`;
            const updated = prev.filter((entry) => entry.id !== entryId);
            return [
                ...updated,
                {
                    id: entryId,
                    projectId: record.id,
                    jobCode: record.jobCode || "",
                    houseId: house.id,
                    sectionName: section.name,
                    amount: value,
                },
            ];
        });
    };

    const clearAssignment = (record: DeviData, house: House, section: { name: string }) => {
        setAssignmentData((prev) =>
            prev.filter((entry) =>
                !(entry.projectId === record.id && entry.houseId === house.id && entry.sectionName === section.name)
            )
        );
    };

    const assignAll = (record: DeviData, house: House, section: { name: string }) => {
        setAssignmentData((prev) => {
            const totalAssigned = prev
                .filter((entry) => entry.projectId === record.id)
                .reduce((sum, entry) => sum + entry.amount, 0);

            const remaining = Number(record.amountDevis) - totalAssigned;
            if (remaining <= 0) return prev;

            return [
                ...prev,
                {
                    id: `${record.id}-${house.id}-${section.name}`,
                    projectId: record.id,
                    jobCode: record.jobCode || "",
                    houseId: house.id,
                    sectionName: section.name,
                    amount: remaining,
                },
            ];
        });
    };

    const filterIncompleteRows = () => {
        if (showIncompleteOnly) {
            setFilteredData(deviData.filter((record) => {
                const totalAssigned = assignmentData
                    .filter((entry) => entry.projectId === record.id)
                    .reduce((sum, entry) => sum + entry.amount, 0);
                return totalAssigned < Number(record.amountDevis); // Show only incomplete rows
            }));
        } else {
            setFilteredData(deviData);
        }
    };

    const mapInstallationLocation = (location: string) => {
        switch (location) {
            case "Innen":
                return "I";
            case "Aussen":
                return "A";
            case "Umgebung":
                return "U";
            default:
                return "";
        }
    };

    async function openCalc() {
        try {
            const response = await axios.get(`${API_BASE_URL}/getDevisCalculationLink?auth=${AUTH_TOKEN}&projectNo=${selectedProject}`);
            if (response.data) {
                window.open(response.data, '_blank');
            } else {
                message.error('Kein gültiger Link erhalten.');
            }
        } catch (error) {
            message.error('Fehler beim Abrufen der Kalkulation');
            console.error(error);
        }
    }

    const houseColumns = useMemo(() => {
        const columns: any[] = [];

        houses.forEach((house) => {
            if (visibleHouses.has(house.id)) {
                columns.push({
                    title: (
                        <div style={{ whiteSpace: "normal", wordBreak: "break-word", textAlign: "center", fontWeight: "bold" }}>
                            {house.name}
                        </div>
                    ),
                    dataIndex: `${house.id}_main`,
                    key: `${house.id}_main`,
                    width: 100,
                    className: "house-column",
                    render: (_: any, record: DeviData) => {
                        const assignedValue = assignmentData.find(
                            (entry) =>
                                entry.projectId === record.id &&
                                entry.houseId === house.id &&
                                entry.sectionName === "Main" // Assignments at house level
                        )?.amount || 0;

                        return (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1px" }}>
                                <Space size={2}> {/* Reduce spacing */}
                                    <Button
                                        icon={<CheckOutlined />}
                                        size="small"
                                        type="default"
                                        style={{ padding: "2px 4px", fontSize: "12px", minWidth: "20px", height: "25px" }} // Smaller size
                                        onClick={() => assignAll(record, house, { name: "Main" })}
                                    />
                                    <Button
                                        icon={<DeleteOutlined />}
                                        type={assignedValue ? "primary" : "default"}
                                        danger={!!assignedValue}
                                        size="small"
                                        style={{ padding: "2px 4px", fontSize: "12px", minWidth: "20px", height: "25px" }} // Smaller size
                                        onClick={() => clearAssignment(record, house, { name: "Main" })}
                                        disabled={!assignedValue}
                                    />
                                </Space>
                                <Input
                                    type="number"
                                    value={assignedValue}
                                    style={{
                                        width: "50px",
                                        height: "25px",
                                        fontSize: "12px",
                                        textAlign: "center",
                                        padding: "2px",
                                    }}
                                    onChange={(e) =>
                                        handleAssignmentChange(record, house, { name: "Main" }, Number(e.target.value))
                                    }
                                />
                            </div>
                        );
                    },
                });

                // Section Columns
                Object.values(house.sections).forEach((sectionArray) => {
                    sectionArray.forEach((section) => {
                        columns.push({
                            title: (
                                <div style={{ whiteSpace: "normal", wordBreak: "break-word", textAlign: "center" }}>
                                    {house.name} {section.name}
                                </div>
                            ),
                            dataIndex: `${house.id}_${section.name}`,
                            key: `${house.id}_${section.name}`,
                            width: 100,
                            className: "section-column",
                            render: (_: any, record: DeviData) => {
                                const assignedValue =
                                    assignmentData.find(
                                        (entry) =>
                                            entry.projectId === record.id &&
                                            entry.houseId === house.id &&
                                            entry.sectionName === section.name
                                    )?.amount || 0;

                                return (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                                        <Space size={2}>
                                            <Button
                                                icon={<CheckOutlined />}
                                                style={{ padding: "2px 4px", fontSize: "12px", minWidth: "20px", height: "25px" }} // Smaller size
                                                size="small"
                                                type="default"
                                                onClick={() => assignAll(record, house, section)}
                                            />
                                            <Button
                                                icon={<DeleteOutlined />}
                                                type={assignedValue ? "primary" : "default"}
                                                danger={!!assignedValue}
                                                style={{ padding: "2px 4px", fontSize: "12px", minWidth: "20px", height: "25px" }} // Smaller size
                                                size="small"
                                                onClick={() => clearAssignment(record, house, section)}
                                                disabled={!assignedValue}
                                            />
                                        </Space>
                                        <Input
                                            type="number"
                                            value={assignedValue}
                                            style={{
                                                width: "50px",
                                                height: "25px",
                                                fontSize: "12px",
                                                textAlign: "center",
                                                padding: "2px",
                                            }}
                                            onChange={(e) =>
                                                handleAssignmentChange(record, house, section, Number(e.target.value))
                                            }
                                        />
                                    </div>
                                );
                            },
                        });
                    });
                });
            }
        });
        return columns;
    }, [houses, visibleHouses, assignmentData]);

    // Default columns
    const defaultColumns = [
        {
            title: "Devi-Position",
            dataIndex: "desc",
            key: "desc",
            fixed: "left",
            width: 100,
            minWidth: 100,
            maxWidth: 100,
            className: "devi-position-column", // Add CSS class for styling
            render: (text: string) => (
                <div className="devi-position-text" title={text}>
                    {text}
                </div>
            ),
        },
        {
            title: "NT",
            dataIndex: "supplement",
            key: "supplement",
            fixed: "left",
            width: 30,
            minWidth: 30,
            maxWidth: 30,
            className: "devi-position-column", // Add CSS class for styling
            render: (text: string) => (
                <div className="devi-position-text" title={text}>
                    {text}
                </div>
            ),
        },
        {
            title: "Titel",
            dataIndex: "title",
            sorter: (a: { title: any }, b: { title: any }) => (a.title || "").localeCompare(b.title || ""),
            key: "title",
            fixed: "left",
            width: 200,
            className: "align-top",
            render: (text: string) => {
                const lines = text.split("\n");
                const previewText = lines.slice(0, 3).join("\n");
                return (
                    <Tooltip title={!showFullText ? text : ""} placement="top">
                        <div style={{
                            whiteSpace: "pre-wrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxHeight: showFullText ? "none" : "4.5em",
                            cursor: "pointer",
                        }}>
                            {showFullText ? text : `${previewText} ${lines.length > 3 ? "[...]" : ""}`}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: "Produkt",
            dataIndex: "jobCode",
            key: "product",
            fixed: "left",
            width: 150,
            className: "align-top",
            sorter: (a: { jobCode: string | number; }, b: { jobCode: string | number; }) =>
                (jobMap[a.jobCode]?.descriptionSpez || "").localeCompare((jobMap[b.jobCode]?.descriptionSpez || "")),
            render: (jobCode: string | number) => {
                const product = jobMap[jobCode]?.descriptionSpez || "Unbekannt";
                return (product);
            }
        },
        {
            title: "EO",
            dataIndex: "jobCode",
            key: "installationLocation",
            fixed: "left",
            width: 50,
            className: "align-top",
            sorter: (a: { jobCode: string | number; }, b: { jobCode: string | number; }) =>
                mapInstallationLocation(jobMap[a.jobCode]?.installationLocation || "").localeCompare(mapInstallationLocation(jobMap[b.jobCode]?.installationLocation || "")),
            render: (jobCode: string | number) => {
                const location = jobMap[jobCode]?.installationLocation || "Innen";
                return (
                    <Tooltip title={`Einbauort: ${mapInstallationLocation(location)}`}>
                        {mapInstallationLocation(location)}
                    </Tooltip>
                );
            }
        },
        {
            title: "Zugewiesen",
            dataIndex: "amountDevis",
            key: "amountDevis",
            fixed: "left",
            width: 80,
            minWidth: 80,
            maxWidth: 80,
            render: (text: string, record: DeviData) => {
                const totalAmount = parseFloat(text) || 0;
                const distributedAmount = assignmentData
                    .filter((entry) => entry.projectId === record.id)
                    .reduce((sum, entry) => sum + entry.amount, 0);
                const EPSILON = 0.001;
                const isFullyAssigned = Math.abs(distributedAmount - totalAmount) < EPSILON;

                return (
                    <div style={{
                        display: "flex",
                        gap: "12px",
                        width: "100%",
                    }}>
            <span style={{
                display: "inline-block",
                width: "70px",
                textAlign: "right",
            }}>
                {distributedAmount.toFixed(1)}/{totalAmount.toFixed(1)}
            </span>
                        {isFullyAssigned ? (
                            <CheckCircleOutlined style={{ color: "green", fontSize: "16px", minWidth: "16px" }} />
                        ) : (
                            <CloseCircleOutlined style={{ color: "red", fontSize: "16px", minWidth: "16px" }} />
                        )}
                    </div>
                );
            }
        }

];

    return (
        <div>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px"}}>
                <div style={{display: "flex", alignItems: "center", gap: "5px"}}>
                    <Tooltip title="Kalk öffnen">
                        <Button
                            type="default"
                            onClick={openCalc}
                            icon={<FileExcelOutlined/>}
                        />
                    </Tooltip>
                    <Tooltip title={showFullText ? "Text einklappen" : "Vollen Text anzeigen"}>
                        <Button
                            type="default"
                            icon={showFullText ? <FullscreenExitOutlined/> : <FullscreenOutlined/>}
                            onClick={() => setShowFullText(!showFullText)}
                            size="small"
                            style={{padding: "2px 6px", fontSize: "12px", height: "24px"}}
                        />
                    </Tooltip>
                    <Tooltip title={showIncompleteOnly ? "Alle anzeigen" : "Nur offene Anzeigen"}>
                        <Button
                            type="default"
                            onClick={() => {
                                setShowIncompleteOnly(!showIncompleteOnly);
                                filterIncompleteRows();
                            }}
                            icon={
                                showIncompleteOnly ? (
                                    <CloseCircleOutlined style={{color: "black"}}/>
                                ) : (
                                    <CheckCircleOutlined style={{color: "black"}}/>
                                )
                            }
                            size="small"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "2px 8px",
                                fontSize: "12px",
                                height: "26px",
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Alle Zuweisungen löschen">
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={clearAllAssignments}
                            size="small"
                            style={{ padding: "2px 8px", fontSize: "12px", height: "26px" }}
                        />
                    </Tooltip>
                </div>
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    flexWrap: "nowrap"
                }}>
                    {/* Installation Location Filter */}
                    <div style={{display: "flex", alignItems: "center", gap: "5px"}}>
                        <strong style={{fontSize: "12px"}}>Einbauort:</strong>
                        <Space>
                            {["I", "A", "U"].map((location) => (
                                <Button
                                    key={location}
                                    onClick={() => toggleLocationVisibility(location)}
                                    type={visibleLocations.has(location) ? "primary" : "default"}
                                    size="small"
                                    style={{padding: "2px 6px", fontSize: "10px", height: "24px", minWidth: "30px"}}
                                >
                                    {location}
                                </Button>
                            ))}
                        </Space>
                        <Button
                            type="default"
                            icon={<DeleteOutlined/>}
                            onClick={() => setVisibleLocations(new Set())} // Unselect all locations
                            size="small"
                            style={{padding: "2px 6px", fontSize: "10px", height: "24px", color: "red"}}
                        />
                    </div>

                    {/* Product Filter */}
                    <div style={{display: "flex", alignItems: "center", gap: "5px"}}>
                        <strong style={{fontSize: "12px"}}>Produkte:</strong>
                        <Space wrap>
                            {Array.from(new Set(deviData
                                .map(record => {
                                    if (!record.jobCode) return "Unbekannt";
                                    return jobMap[record.jobCode]?.descriptionSpez || "Unbekannt";
                                })
                            )).map((product) => (
                                <Button
                                    key={product}
                                    onClick={() => toggleProductVisibility(product)}
                                    type={visibleProducts.has(product) ? "primary" : "default"}
                                    size="small"
                                    style={{padding: "2px 6px", fontSize: "10px", height: "24px", minWidth: "50px"}}
                                >
                                    {product}
                                </Button>
                            ))}
                        </Space>
                        <Button
                            type="default"
                            icon={<DeleteOutlined/>}
                            onClick={() => setVisibleProducts(new Set())} // Unselect all products
                            size="small"
                            style={{padding: "2px 6px", fontSize: "10px", height: "24px", color: "red"}}
                        />
                    </div>

                    {/* House Filter (Bauabschnitt) */}
                    <div style={{display: "flex", alignItems: "center", gap: "5px"}}>
                        <strong style={{fontSize: "12px"}}>Bauabschnitt:</strong>
                        <Space>
                            {houses.map((house) => (
                                <Button
                                    key={house.id}
                                    onClick={() => toggleHouseVisibility(house.id)}
                                    type={visibleHouses.has(house.id) ? "primary" : "default"}
                                    size="small"
                                    style={{padding: "2px 6px", fontSize: "10px", height: "24px", minWidth: "50px"}}
                                >
                                    {house.name}
                                </Button>
                            ))}
                        </Space>
                        <Button
                            type="default"
                            icon={<DeleteOutlined/>}
                            onClick={() => setVisibleHouses(new Set())} // Unselect all houses
                            size="small"
                            style={{padding: "2px 6px", fontSize: "10px", height: "24px", color: "red"}}
                        />
                    </div>
                </div>
            </div>
            <div style={{overflowX: "auto", width: "100%"}}>
                <Table
                    className="construction-table"
                    columns={[...defaultColumns, ...houseColumns]}
                    dataSource={filteredData}
                    rowKey="id"
                    pagination={{pageSize: 100}}
                    scroll={{x: Math.max(1200, houseColumns.length * 50), y: 600}}
                    tableLayout="fixed"
                    style={{minWidth: "1200px", whiteSpace: "nowrap"}}
                />
            </div>

        </div>
    );
};

export default StepThree;
